/*
 *
 *  * Copyright (c) [hoarfroster-frontend] 2026. All Rights Reserved.
 *  *
 *  * Last Modified on Jun 21, 2026 by hoarfroster
 *
 */

import { type ChangeEvent, type CompositionEvent, type InputHTMLAttributes, useEffect, useMemo, useState } from 'react';

type GoodInputProps = Omit<InputHTMLAttributes<HTMLInputElement>, 'onChange'> & {
    label?: string;
    optional?: boolean;
    className?: string;
    onChange?: (value: string | null | undefined, event?: ChangeEvent<HTMLInputElement>) => void;
    name: string;
    type?: string;
};

export default function GoodInput({
    label,
    name,
    defaultValue,
    value,
    onChange,
    type = 'text',
    placeholder,
    required = false,
    disabled = false,
    optional = false,
    className = '',
    id,
    ...props
}: GoodInputProps) {
    // Prevent usage of date/datetime inputs with GoodInput — use dedicated date/time component.
    if (type === 'date' || type === 'datetime-local') {
        throw new Error(
            'GoodInput does not support date or datetime-local types. Use the unified GoodDateInput component from @components/form/date-time.'
        );
    }
    // Disallow checkbox usage here — use GoodCheckbox for boolean inputs.
    if (type === 'checkbox') {
        throw new Error('GoodInput does not support checkbox type. Use GoodCheckbox from @components/form instead.');
    }
    const inputId = id ?? name;

    const isControlled = useMemo(() => value !== undefined, [value]);

    const [inputValue, setInputValue] = useState<string | readonly string[] | undefined>(() => {
        const next = value ?? defaultValue ?? '';
        return Array.isArray(next) ? next : String(next);
    });

    const [focusing, setFocusing] = useState(false);
    const [isComposing, setIsComposing] = useState(false);

    // Sync controlled values from parent state.
    useEffect(() => {
        if (!isControlled) return;
        // During IME composition we must not overwrite the in-progress DOM value,
        // otherwise composition breaks and the input can appear "frozen".
        if (isComposing) return;
        const next = value ?? '';
        setInputValue(Array.isArray(next) ? next : String(next));
    }, [isComposing, isControlled, value]);

    // Reset uncontrolled inputs when defaultValue changes (e.g., switching records).
    useEffect(() => {
        if (isControlled) return;
        const next = defaultValue ?? '';
        setInputValue(Array.isArray(next) ? next : String(next));
    }, [defaultValue, isControlled]);

    const handleTrashClick = () => {
        const cleared = '';
        setInputValue(cleared);
        onChange?.(undefined);
    };

    const handleCompositionStart = () => setIsComposing(true);

    const handleCompositionEnd = (ev: CompositionEvent<HTMLInputElement>) => {
        setIsComposing(false);
        const next = ev.currentTarget.value;
        // Always update our internal value so the composed text is reflected immediately,
        // even when the parent is controlling the field.
        setInputValue(next);
        onChange?.(next);
    };

    const handleChange = (ev: ChangeEvent<HTMLInputElement>) => {
        const next = ev.target.value;
        // Update internal value on every keystroke so IME composition works even
        // when the input is controlled by parent state.
        setInputValue(next);
        if (!isComposing && !(ev.nativeEvent as unknown as { isComposing?: boolean }).isComposing) {
            onChange?.(next, ev);
        }
    };

    return (
        <div>
            {label && (
                <label className="text-theme-text-light block text-sm font-medium" htmlFor={inputId}>
                    {label}
                </label>
            )}
            <div
                className={`border-theme-input-border bg-theme-input mt-1 flex min-h-9 items-center justify-center rounded-md border transition-[border-color,box-shadow,background-color] outline-none sm:text-sm ${focusing ? 'border-theme-focus ring-theme-focus/20 ring-2' : ''} ${disabled ? 'opacity-60' : ''}`}
            >
                <input
                    id={inputId}
                    name={name}
                    className={`text-theme-text placeholder:text-theme-text-muted block w-full flex-1 bg-transparent px-2.5 py-1.5 text-sm outline-none ${className ?? ''}`}
                    type={type}
                    value={Array.isArray(inputValue) ? '' : (inputValue ?? '')}
                    onChange={handleChange}
                    onCompositionStart={handleCompositionStart}
                    onCompositionEnd={handleCompositionEnd}
                    onFocus={() => setFocusing(true)}
                    onBlur={() => {
                        setFocusing(false);
                        setIsComposing(false);
                    }}
                    placeholder={placeholder}
                    required={required}
                    disabled={disabled}
                    {...props}
                />
                {optional && (
                    <span
                        className="material-symbols-rounded hover:bg-theme-hover mr-1 ml-1 block h-6 w-6 cursor-pointer rounded-md p-1 text-center align-middle text-sm!"
                        onClick={handleTrashClick}
                    >
                        delete
                    </span>
                )}
            </div>
        </div>
    );
}
